import { useState } from 'react';
import Modal from './Modal.tsx';
import type { Person } from '../../shared/organisation.ts';

/**
 * Who can open a deck in Studio.
 *
 * Three states, and they are the three the server stores (`restrictedTo` in
 * studio.json): everyone, the owner alone, or the owner plus named people.
 * Presented as three radios rather than a checkbox plus a list, because
 * "private" and "shared with two people" are one question with three answers,
 * and a checkbox that silently keeps a stale list is how a deck ends up shared
 * with somebody who left.
 *
 * It does not gate the PUBLISHED /p/ link, which has its own password and may
 * already be with a client. The footnote says so, because "private" reads like
 * a promise about the whole deck and here it is only about Studio.
 */
export type DeckVisibilityChoice = string[] | null;

type DeckVisibilityModalProps = {
  deck: { title: string; ownerId: string | null; restrictedTo: string[] | null };
  /** The registry, for the share list. Archived people are dropped unless the
   *  deck is already shared with them: taking somebody's access away is the
   *  owner's decision to make, not a side effect of them being archived. */
  people: Person[];
  /** The signed-in person, for the wording on an unowned deck: restricting one
   *  claims it, so the dialog has to say whose it is about to become. */
  me: Person | null;
  busy?: boolean;
  /** Opened from inside another dialog (the Publish drawer), so Escape closes
   *  this one and stops there rather than taking the drawer with it. */
  nested?: boolean;
  onCancel: () => void;
  onSave: (next: DeckVisibilityChoice) => void;
};

type Mode = 'everyone' | 'private' | 'shared';

export default function DeckVisibilityModal({
  deck,
  people,
  me,
  busy = false,
  nested = false,
  onCancel,
  onSave,
}: DeckVisibilityModalProps) {
  const initial: Mode = deck.restrictedTo === null ? 'everyone' : deck.restrictedTo.length === 0 ? 'private' : 'shared';
  const [mode, setMode] = useState<Mode>(initial);
  // Kept across a switch to Everyone and back, so changing your mind twice does
  // not cost you the list you just built. Only the CHOSEN mode is submitted.
  const [picked, setPicked] = useState<string[]>(() => [...(deck.restrictedTo ?? [])]);

  const already = new Set(deck.restrictedTo ?? []);
  // Restricting an unowned deck claims it (see the route), so from the moment a
  // restricting option is chosen the owner-to-be is the reader. Every deck made
  // before owner-on-create is unowned, so this is the common case rather than
  // the edge one.
  const claiming = !deck.ownerId && mode !== 'everyone';
  const owner = people.find((p) => p.id === deck.ownerId) ?? (claiming ? me : null);
  const ownerName = owner ? (owner.id === me?.id ? 'You' : owner.name) : 'The owner';
  const excludeId = deck.ownerId ?? (claiming ? me?.id : null);
  const options = people.filter((p) => p.id !== excludeId && (!p.archived || already.has(p.id)));

  function toggle(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  function submit() {
    if (mode === 'everyone') return onSave(null);
    if (mode === 'private') return onSave([]);
    onSave(picked);
  }

  return (
    <Modal
      title="Privacy"
      nested={nested}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={submit}
            disabled={busy || (mode === 'shared' && picked.length === 0)}
          >
            Save
          </button>
        </>
      }
    >
      <p className="muted small" style={{ marginBottom: '0.6rem' }}>
        {deck.title}
      </p>
      <ul className="cat-picker" role="list">
        <li>
          <label className="cat-picker__row">
            <input
              type="radio"
              name="deck-visibility"
              checked={mode === 'everyone'}
              onChange={() => setMode('everyone')}
            />
            <span>Everyone at RKH</span>
          </label>
        </li>
        <li>
          <label className="cat-picker__row">
            <input
              type="radio"
              name="deck-visibility"
              checked={mode === 'private'}
              onChange={() => setMode('private')}
            />
            <span>{owner && owner.id !== me?.id ? `Only ${owner.name}` : 'Only me'}</span>
          </label>
        </li>
        <li>
          <label className="cat-picker__row">
            <input type="radio" name="deck-visibility" checked={mode === 'shared'} onChange={() => setMode('shared')} />
            <span>Specific people</span>
          </label>
        </li>
      </ul>
      {mode === 'shared' && (
        <ul className="cat-picker" role="list">
          {options.map((p) => (
            <li key={p.id}>
              <label className="cat-picker__row">
                <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} />
                <span>
                  {p.name}
                  {p.archived ? ' (archived)' : ''}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {/* Said before the Save, not after it: claiming a deck is a change the
          reader did not explicitly ask for, so it cannot be a surprise. */}
      {claiming && (
        <p className="muted small" style={{ marginTop: '0.5rem' }}>
          This deck has no owner yet. Saving makes it yours, which is what lets you decide who else sees it.
        </p>
      )}
      <p className="muted small" style={{ marginTop: '0.5rem' }}>
        {mode === 'everyone'
          ? 'Anyone signed in to Studio can open it.'
          : mode === 'shared' && picked.length === 0
            ? 'Pick at least one person, or choose one of the options above.'
            : `${ownerName} and studio admins can always open it. A published link is not affected: it has its own password.`}
      </p>
    </Modal>
  );
}
